import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DataApi from '../../api/DataApi';
import AppToast from '../AppToast';
import './css/PermissionList.css';
import jwt_decode from 'jwt-decode';

const PermissionList = () => {
  const { id: roleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState(null);
  const [selectedTabNames, setSelectedTabNames] = useState([]);
  const [allowedStandardTabs, setAllowedStandardTabs] = useState([]);
  const [allowedCustomTabs, setAllowedCustomTabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [userRole, setUserRole] = useState('');
  const permissionApi = new DataApi('permissions');
  const roleApi = new DataApi('roles');

  useEffect(() => {
    if (roleId) {
      fetchRole();
      fetchPermissions();
    }
  }, [roleId]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwt_decode(token);
        console.log('Decoded token:', decoded);
        setUserRole(decoded?.role_name || '');
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }
  }, []);

  // Check for success message in URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const success = searchParams.get('success');
    const message = searchParams.get('message');

    if (success === 'true' && message) {
      setToastMessage(decodeURIComponent(message));
      setToastVariant('success');
      setShowToast(true);

      // Clear the URL parameters
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, navigate, location.pathname]);

  const fetchRole = async () => {
    try {
      const response = await roleApi.fetchById(roleId);
      console.log('Role response:', response);
      if (response.data.success) {
        setRole(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching role:', err);
      setError('Failed to fetch role information');
    }
  };

  const fetchPermissions = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching permissions for roleId:', roleId);
      const response = await permissionApi.get(`/role/${roleId}`);
      console.log('Permissions response:', response);
      if (response.data.success) {
        setPermissions(response.data.data);
        setSelectedTabNames(Array.isArray(response.data.selectedTabNames) ? response.data.selectedTabNames : []);
        setAllowedStandardTabs(Array.isArray(response.data.allowedStandardTabs) ? response.data.allowedStandardTabs : []);
        setAllowedCustomTabs(Array.isArray(response.data.allowedCustomTabs) ? response.data.allowedCustomTabs : []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch permissions');
      console.error('Error fetching permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (permission) => {
    navigate(`/setup/permission/${roleId}/edit/${permission.id}`);
  };

  const handleCreate = (permission) => {
    // Navigate to create permission page with pre-filled data
    navigate(`/setup/permission/${roleId}/create`, {
      state: {
        permissionData: {
          role_id: roleId,
          object_name: permission.object_name,
          can_create: false,
          can_read: false,
          can_update: false,
          can_delete: false,
          is_active: true
        }
      }
    });
  };

  const handleBack = () => {
    navigate('/setup/rolemanager');
  };

  const handleToastClose = () => {
    setShowToast(false);
  };

  // Selected tabs split by standard vs custom; resolve names to labels for display
  const matchTab = (name, tabList) =>
    tabList.find(
      (t) =>
        String(name).toLowerCase() === String(t.name || '').toLowerCase() ||
        String(name).toLowerCase() === String(t.label || '').toLowerCase()
    );
  const selectedStandardTabs = selectedTabNames
    .map((name) => matchTab(name, allowedStandardTabs))
    .filter(Boolean)
    .map((t) => ({ name: t.name, label: t.label || t.name }));
  const selectedCustomTabs = selectedTabNames
    .map((name) => matchTab(name, allowedCustomTabs))
    .filter(Boolean)
    .map((t) => ({ name: t.name, label: t.label || t.name }));

  if (loading) {
    return (
      <div className="permission-list-page">
        <div className="loading">Loading permissions...</div>
      </div>
    );
  }

  return (
    <div className="permission-list-page">
      <div className="permission-list-header">
        <div className="header-left">
          <button className="btn-back" onClick={handleBack}>
            <i className="fas fa-arrow-left"></i>
            Back to Roles
          </button>
          <div className="header-info">
            <h1>View Permissions</h1>
            <p>Role: {role?.fields?.find(f => f.field_name === 'name')?.value || role?.name || 'Loading...'}</p>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {!loading && permissions.length === 0 && (
        <div className="no-permissions">
          <div className="permissions-tabs-section permissions-tabs-two-sections">
            <div className="tabs-subsection">
              <h4 className="tabs-subsection-title">Standard Tabs</h4>
              <div className="tabs-label-list">
                {selectedStandardTabs.length === 0 ? (
                  <span className="tabs-empty-hint">None selected</span>
                ) : (
                  selectedStandardTabs.map((t) => (
                    <span key={t.name} className="tab-label">
                      {t.label}
                    </span>
                  ))
                )}
              </div>
            </div>
            <div className="tabs-subsection">
              <h4 className="tabs-subsection-title">Custom Tabs</h4>
              <div className="tabs-label-list">
                {selectedCustomTabs.length === 0 ? (
                  <span className="tabs-empty-hint">None selected</span>
                ) : (
                  selectedCustomTabs.map((t) => (
                    <span key={t.name} className="tab-label">
                      {t.label}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>
          <p>No object permissions found for this role.</p>
          <button className="btn btn-primary" onClick={() => navigate(`/setup/permission/${roleId}/create`)}>
            Add First Permission
          </button>
          <button className="btn btn-secondary ms-2" onClick={() => navigate(`/setup/permission/${roleId}/bulk-edit`)}>
            Edit Permissions (incl. Tabs)
          </button>
        </div>
      )}

      {!loading && permissions.length > 0 && (
        <div className="permissions-content">
          <div className="permissions-tabs-actions">
            {(() => {
              const roleName = role?.fields?.find(f => f.field_name === 'name')?.value;
              const shouldHideEdit = userRole === roleName && roleName === 'ADMIN';
              return !shouldHideEdit ? (
                <button
                  className="btn btn-success"
                  onClick={() => navigate(`/setup/permission/${roleId}/bulk-edit`)}
                >
                  Edit Permissions
                </button>
              ) : null;
            })()}
          </div>
          <div className="permissions-tabs-section permissions-tabs-two-sections">
            <div className="tabs-subsection">
              <h4 className="tabs-subsection-title">Standard Tabs</h4>
              <div className="tabs-label-list">
                {selectedStandardTabs.length === 0 ? (
                  <span className="tabs-empty-hint">None selected</span>
                ) : (
                  selectedStandardTabs.map((t) => (
                    <span key={t.name} className="tab-label">
                      {t.label}
                    </span>
                  ))
                )}
              </div>
            </div>
            <div className="tabs-subsection">
              <h4 className="tabs-subsection-title">Custom Tabs</h4>
              <div className="tabs-label-list">
                {selectedCustomTabs.length === 0 ? (
                  <span className="tabs-empty-hint">None selected</span>
                ) : (
                  selectedCustomTabs.map((t) => (
                    <span key={t.name} className="tab-label">
                      {t.label}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>
          <div className="permissions-header">
            <h3>Object Permissions</h3>
          </div>

          {(() => {
            const standardPermissions = permissions.filter((p) => p.is_standard === true);
            const customPermissions = permissions.filter((p) => p.is_standard !== true);
            const renderRow = (permission) => (
              <tr key={permission.id || permission.object_name} className={!permission.is_existing ? 'missing-permission' : ''}>
                <td className="object-cell">
                  <div className="object-name">
                    {permission.object_display_name || permission.object_name}
                    {!permission.is_existing && (
                      <span className="badge-new">New</span>
                    )}
                  </div>
                </td>
                <td className="checkbox-cell">
                  <input type="checkbox" checked={permission.can_create || false} disabled className="permission-checkbox" />
                </td>
                <td className="checkbox-cell">
                  <input type="checkbox" checked={permission.can_read || false} disabled className="permission-checkbox" />
                </td>
                <td className="checkbox-cell">
                  <input type="checkbox" checked={permission.can_update || false} disabled className="permission-checkbox" />
                </td>
                <td className="checkbox-cell">
                  <input type="checkbox" checked={permission.can_delete || false} disabled className="permission-checkbox" />
                </td>
                <td className="checkbox-cell">
                  <input type="checkbox" checked={permission.view_all || false} disabled className="permission-checkbox" />
                </td>
                <td className="checkbox-cell">
                  <input type="checkbox" checked={permission.modify_all || false} disabled className="permission-checkbox" />
                </td>
              </tr>
            );
            return (
              <div className="permissions-table-container">
                <table className="permissions-table">
                  <thead>
                    <tr>
                      <th className="object-column text-dark">Object</th>
                      <th className="checkbox-column">
                        <div className="header-checkbox">
                          <span className='text-dark'><i className="fas fa-plus text-dark"></i> Create</span>
                        </div>
                      </th>
                      <th className="checkbox-column">
                        <div className="header-checkbox">
                          <span className='text-dark'><i className="fas fa-eye"></i> Read</span>
                        </div>
                      </th>
                      <th className="checkbox-column">
                        <div className="header-checkbox">
                          <span className='text-dark'><i className="fas fa-edit text-dark"></i> Update</span>
                        </div>
                      </th>
                      <th className="checkbox-column">
                        <div className="header-checkbox">
                          <span className='text-dark'><i className="fas fa-trash text-dark"></i> Delete</span>
                        </div>
                      </th>
                      <th className="checkbox-column">
                        <div className="header-checkbox">
                          <span className='text-dark'><i className="fas fa-eye text-dark"></i> View All</span>
                        </div>
                      </th>
                      <th className="checkbox-column">
                        <div className="header-checkbox">
                          <span className='text-dark'><i className="fas fa-edit text-dark"></i> Modify All</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {standardPermissions.length > 0 && (
                      <>
                        <tr className="permission-section-header">
                          <td colSpan="7" className="section-header-cell">Standard Objects</td>
                        </tr>
                        {standardPermissions.map(renderRow)}
                      </>
                    )}
                    {customPermissions.length > 0 && (
                      <>
                        <tr className="permission-section-header">
                          <td colSpan="7" className="section-header-cell">Custom Objects</td>
                        </tr>
                        {customPermissions.map(renderRow)}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {/* Success Toast */}
      <AppToast
        show={showToast}
        message={toastMessage}
        variant={toastVariant}
        onClose={handleToastClose}
      />
    </div>
  );
};

export default PermissionList;
